import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, MapPin, Star, Flag, Ban, Loader2, ShieldAlert } from "lucide-react";
import { Avatar } from "@/components/CompanionCard";
import { BookingPanel } from "@/components/BookingPanel";
import { ReportDialog } from "@/components/ReportDialog";
import { api, errMsg, getActivities, inr, fmtDateTime } from "@/lib/api";
import { useLang, actLabel } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const WeekTable = ({ week }) => (
  <div className="grid grid-cols-7 gap-1.5 text-center" data-testid="weekly-availability">
    {DAYS.map((d, i) => {
      const w = week[String(i)];
      return (
        <div key={d} className={`rounded-lg py-2 text-xs ${w ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-400"}`}>
          <p className="font-semibold">{d}</p>
          <p className="font-mono text-[10px] mt-0.5">{w ? `${w.start}–${w.end}` : "Off"}</p>
        </div>
      );
    })}
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-3"><h3 className="text-xl font-semibold">{title}</h3>{children}</div>
);

const Actions = ({ c }) => {
  const { user } = useAuth();
  const { t } = useLang();
  if (user?.role !== "customer") return null;
  const block = async () => {
    try {
      await api.post("/blocks", { companion_id: c.id });
      toast.success("Companion blocked. They will no longer appear in your results.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex gap-2">
      <ReportDialog companionId={c.id} trigger={<button data-testid="report-companion-btn" className="chip hover:border-rose-400"><Flag size={12} className="mr-1" />{t("report")}</button>} />
      <button data-testid="block-companion-btn" onClick={block} className="chip hover:border-rose-400"><Ban size={12} className="mr-1" />{t("block")}</button>
    </div>
  );
};

export default function CompanionProfile() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  const [acts, setActs] = useState([]);

  useEffect(() => {
    getActivities().then(setActs).catch(() => {});
    api.get(`/companions/${id}`).then((r) => setC(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="container-x py-24 text-center" data-testid="profile-not-found"><h1 className="text-4xl font-bold">Profile not available</h1><Link to="/companions" className="text-[#0D5C46] font-semibold mt-4 inline-block">← Back to companions</Link></div>;
  if (!c) return <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-[#0D5C46]" /></div>;

  return (
    <div className="container-x py-10 grid lg:grid-cols-12 gap-10" data-testid="companion-profile-page">
      <div className="lg:col-span-7 space-y-8">
        <div className="grid sm:grid-cols-5 gap-6">
          <div className="sm:col-span-2 relative">
            <Avatar c={c} className="w-full aspect-[4/5] rounded-2xl" />
            {c.is_demo && <span className="demo-badge absolute top-3 left-3" data-testid="profile-demo-badge">DEMO PROFILE</span>}
          </div>
          <div className="sm:col-span-3 space-y-4">
            {c.verified && <span className="verified-badge" data-testid="profile-verified-badge"><BadgeCheck size={14} />{t("verified")}</span>}
            <h1 className="text-5xl font-bold" data-testid="profile-name">{c.display_name}, <span className="font-sans text-3xl text-slate-500">{c.age}</span></h1>
            <p className="flex items-center gap-1.5 text-slate-600"><MapPin size={16} />{c.city}, Jharkhand</p>
            <p className="flex items-center gap-1.5 text-sm"><Star size={16} className="fill-amber-400 text-amber-400" /><b>{c.rating?.toFixed(1)}</b><span className="text-slate-500">· {c.review_count} {t("reviews")}</span></p>
            <p className="font-mono text-2xl font-semibold text-[#0D5C46]" data-testid="profile-price">{inr(c.hourly_rate)}<span className="text-sm text-slate-500">{t("per_hour")}</span></p>
            <Actions c={c} />
          </div>
        </div>
        <Section title={t("about")}><p className="text-slate-600 leading-relaxed">{c.bio}</p></Section>
        <div className="grid sm:grid-cols-2 gap-8">
          <Section title={t("languages")}><div className="flex flex-wrap gap-2">{c.languages.map((l) => <span key={l} className="chip">{l}</span>)}</div></Section>
          <Section title={t("interests")}><div className="flex flex-wrap gap-2">{c.interests.map((l) => <span key={l} className="chip">{l}</span>)}</div></Section>
        </div>
        <Section title={t("activities")}><div className="flex flex-wrap gap-2" data-testid="profile-activities">{c.activities.map((s) => <span key={s} className="chip bg-emerald-50 border-emerald-200 text-emerald-800">{actLabel(acts.find((a) => a.slug === s), lang) || s}</span>)}</div></Section>
        <Section title={`${t("availability")} (IST)`}><WeekTable week={c.weekly_availability} /></Section>
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-900 flex gap-3">
          <ShieldAlert size={20} className="shrink-0" />
          <p>Platonic companionship only. Meet in public places. Sexual services, harassment or any illegal request will result in a permanent ban and may be reported to authorities. Personal phone numbers and addresses are never shared.</p>
        </div>
        <Section title={`${lang === "en" ? "Reviews" : t("reviews")} (${c.reviews.length})`}>
          <div className="space-y-3" data-testid="profile-reviews">
            {c.reviews.map((r) => (
              <div key={r.id} className="card-surface p-5">
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">{r.reviewer} {r.is_demo && <span className="demo-badge ml-1">DEMO</span>}</span>
                  <span className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={14} className={i < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"} />)}</span>
                </div>
                <p className="text-sm text-slate-600 mt-2">{r.comment}</p>
                <p className="text-xs text-slate-400 mt-2">{fmtDateTime(r.created_at)}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>
      <aside className="lg:col-span-5"><div className="lg:sticky lg:top-24"><BookingPanel c={c} acts={acts} /></div></aside>
    </div>
  );
}
